import { z } from "zod";
import { ESTIMATE_MSG, Estimate, EstimateLine, EstimateSave, LineInput, SheetInput } from "./estimate.js";
import { Plan, planSpec } from "./billing.js";
import { RoundingCode } from "./rounding.js";

// ---------------------------------------------------------------------------
// 見積書の雛形（023。#197 で画面の名前を「見積の雛形」から）。よく使う見積の「中身」（内訳の紙＋行＋紙の備考）を
// 保存して、新しい見積の下書きの初期値にする。帳票デザインのテンプレート（010 document_templates）とは別物。
// 中身は見積エディタのモデル（EstimateSave の sheets＋lines）をそのまま JSON で持ち、
// 行のスキーマが変わっても API の読み込みアダプタ（schema_version）で吸収する。
// 紙の備考（notes）は頭の欄なのでタイトルと同じく列（estimate_templates.notes、20261017_1300）。行ごとの備考は lines[].note
// ---------------------------------------------------------------------------

export const TEMPLATE_MSG = {
  notFound: "そのテンプレートは見つかりません",
  titleRequired: "テンプレート名を入力してください",
  titleTooLong: "テンプレート名は100文字までです",
  limitReached: (limit: number) => `テンプレートは最大${limit}件までです（現在のプラン）`,
  // ---- 画面の名前（#197、docs/78・docs/10 §5.1.2。2026-10-17 夕に「雛形」から「テンプレート」へ）：設定のメニュー・一覧の題・編集画面の戻り先＝listTitle、
  //      編集画面の題＝editTitle、一覧の作成のボタンと窓の題＝create、名前の欄＝titleLabel。画面に「雛形」と書かない（工程表・場所の雛形は別の機能） ----
  listTitle: "見積書のテンプレート",
  editTitle: "テンプレートの編集",
  create: "テンプレート作成",
  titleLabel: "テンプレート名",
  /** 編集画面の題の右の「？」（{customer}・{subject} は画面の t() が会社の言葉に） */
  autoSaved: "テンプレートは自動で保存されます。",
  editHelp: "ここで入力した明細と備考が、「新しい見積書 → テンプレートから」で下書きの初期値になります。{customer}・{subject}・日付は見積書の作成時に設定します。",
  /** 一覧の戻り先が無いとき（開けなかった画面） */
  backToList: "見積書テンプレートへ",
  // ---- 新しい見積書の作り方（EstimateNew.tsx）----
  fromTemplate: "テンプレートから",
  pickLabel: "テンプレート",
  count: (n: number) => `テンプレート ${n}件`,
  none: "まだテンプレートはありません。",
  loading: "テンプレートを読み込んでいます…",
  newHelp: "明細は次の画面で入力します。テンプレートから作成すると、テンプレートの中身が入った下書きになります（テンプレートそのものは変わりません）。",
  paperNote: (title: string, lines: number) => `テンプレート「${title}」の ${lines} 行が入ります`,
} as const;

/** プラン別の雛形の上限。DB は保存先のみで、判定は API がこの定数で行う。数は料金表（billing.ts の PLAN_CATALOG）に1か所だけ */
export const ESTIMATE_TEMPLATE_LIMITS: Record<Plan, number> = {
  personal: planSpec("personal").templates,
  small: planSpec("small").templates,
  business: planSpec("business").templates,
};

/** 作成・更新の中身。sheets・lines・notes は見積の保存（EstimateSave）と同じ明細モデル・同じ長さの決まりを再利用 */
export const EstimateTemplateInput = z.object({
  title: z.string().trim().min(1, TEMPLATE_MSG.titleRequired).max(100, TEMPLATE_MSG.titleTooLong),
  /** 紙に印字する備考（#197）。雛形から見積書を作ると版の備考に写る。省けば空（前からの送り方） */
  notes: EstimateSave.shape.notes,
  sheets: z.array(SheetInput).max(50, ESTIMATE_MSG.sheetsTooMany).default([]),
  lines: z.array(LineInput).max(500, ESTIMATE_MSG.tooManyLines).default([]),
});
export type EstimateTemplateInput = z.infer<typeof EstimateTemplateInput>;

/** content_json に入れる形（schema_version=1）。読み込み時のアダプタもこれで検証する */
export const EstimateTemplateContent = z.object({
  sheets: z.array(SheetInput).max(50).default([]),
  lines: z.array(LineInput).max(500).default([]),
});
export type EstimateTemplateContent = z.infer<typeof EstimateTemplateContent>;

/** 一覧の1件 */
export const EstimateTemplateSummary = z.object({
  id: z.string().uuid(),
  title: z.string(),
  lineCount: z.number().int(),
  updatedAt: z.string(),
});
export type EstimateTemplateSummary = z.infer<typeof EstimateTemplateSummary>;

/** 一覧。limit は現在のプランの上限（残り＝limit − items.length） */
export const EstimateTemplateList = z.object({
  items: z.array(EstimateTemplateSummary),
  plan: Plan,
  limit: z.number().int(),
});
export type EstimateTemplateList = z.infer<typeof EstimateTemplateList>;

/**
 * 1件の中身。行の金額・紙の小計・税は、見積と同じ DB の計算（fn_recalc_all）を
 * 仮の紙で流して求めた表示用の値（保存はされない。雛形の中身は sheets＋lines だけ）
 */
export const EstimateTemplate = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /** 紙に印字する備考（#197。空なら ""） */
  notes: z.string(),
  updatedAt: z.string(),
  lines: z.array(EstimateLine),
  sheets: z.array(SheetInput.extend({ no: z.number().int(), subtotal: z.number().int() })),
  calc: z.object({
    lineRoundingMode: RoundingCode,
    roundingMode: RoundingCode,
    subtotal: z.number().int(),
    taxTotal: z.number().int(),
    /** 立替金の合計（T1。subtotal・taxTotal に入らず total に入る） */
    reimbursementTotal: z.number().int(),
    total: z.number().int(),
    taxes: Estimate.shape.taxes,
  }),
});
export type EstimateTemplate = z.infer<typeof EstimateTemplate>;
